import { Outlet, useNavigate, useLocation } from "react-router";
import { useAuthStore } from "~/ui/auth/authStore";
import { OperioBrand } from "~/ui/brand/OperioBrand";

export function AppLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { clearAuth, email, isAdmin } = useAuthStore();

  const isActive = (path: string) => location.pathname.startsWith(path);

  const handleLogout = () => {
    clearAuth();
    navigate("/login");
  };

  return (
    <div className="flex min-h-screen flex-col">
      {/* Sidebar + Main */}
      <div className="flex flex-1">
        <nav className="w-64 border-r border-gray-200 bg-white p-4">
          <div className="mb-8">
            <OperioBrand compact />
            {email && email !== "undefined" && (
              <p className="mt-1 text-sm text-gray-600">{email}</p>
            )}
          </div>

          <div className="space-y-2">
            <NavLink
              icon="patients"
              label="Kunden"
              isActive={isActive("/app/customers")}
              onClick={() => navigate("/app/customers")}
            />
            <NavLink
              icon="catalog"
              label="Leistungskatalog"
              isActive={isActive("/app/catalog")}
              onClick={() => navigate("/app/catalog")}
            />
            <NavLink
              icon="invoices"
              label="Rechnungen"
              isActive={isActive("/app/invoices")}
              onClick={() => navigate("/app/invoices")}
            />
            <NavLink
              icon="reports"
              label="Berichte"
              isActive={isActive("/app/reports")}
              onClick={() => navigate("/app/reports")}
            />
            <NavLink
              icon="settings"
              label="Einstellungen"
              isActive={isActive("/app/settings")}
              onClick={() => navigate("/app/settings")}
            />
            {isAdmin && (
              <NavLink
                icon="admin"
                label="Admin"
                isActive={isActive("/app/admin")}
                onClick={() => navigate("/app/admin")}
              />
            )}
            <NavLink
              icon="logout"
              label="Abmelden"
              isActive={false}
              onClick={handleLogout}
            />
          </div>
        </nav>

        {/* Main Content */}
        <main className="flex-1 bg-gray-50">
          <Outlet />
        </main>
      </div>

      {/* Footer */}
      <footer className="border-t border-gray-200 bg-white py-4">
        <div className="flex justify-center gap-6 text-sm">
          <a href="/impressum" className="text-blue-600 hover:text-blue-700">
            Impressum
          </a>
          <a href="/datenschutz" className="text-blue-600 hover:text-blue-700">
            Datenschutz
          </a>
        </div>
      </footer>
    </div>
  );
}

function NavLink({
  icon,
  label,
  isActive,
  onClick,
}: {
  icon: IconName;
  label: string;
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex w-full items-center gap-3 rounded-md px-4 py-2 text-left text-sm font-medium transition-colors ${
        isActive
          ? "bg-blue-100 text-blue-900"
          : "text-gray-700 hover:bg-gray-100"
      }`}
    >
      <NavIcon name={icon} />
      {label}
    </button>
  );
}

type IconName =
  "patients" | "catalog" | "invoices" | "reports" | "settings" | "admin" | "logout";

function NavIcon({ name }: { name: IconName }) {
  const paths: Record<IconName, React.ReactNode> = {
    patients: (
      <>
        <circle cx="9" cy="7" r="3" />
        <path d="M3.5 19a5.5 5.5 0 0 1 11 0M17 8v6M14 11h6" />
      </>
    ),
    catalog: (
      <>
        <path d="M4 5h16v14H4zM8 9h8M8 13h5" />
        <path d="M7 3v4M17 3v4" />
      </>
    ),
    invoices: (
      <>
        <path d="M6 3h9l3 3v15H6zM15 3v4h4" />
        <path d="M9 11h6M9 15h6" />
      </>
    ),
    reports: <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />,
    settings: (
      <>
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1-2.8 2.8-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.6v.2h-4V21a1.7 1.7 0 0 0-1-1.6 1.7 1.7 0 0 0-1.9.3l-.1.1L4.2 17l.1-.1a1.7 1.7 0 0 0 .3-1.9A1.7 1.7 0 0 0 3 14H2.8v-4H3a1.7 1.7 0 0 0 1.6-1 1.7 1.7 0 0 0-.3-1.9L4.2 7 7 4.2l.1.1A1.7 1.7 0 0 0 9 4.6a1.7 1.7 0 0 0 1-1.6v-.2h4V3a1.7 1.7 0 0 0 1 1.6 1.7 1.7 0 0 0 1.9-.3l.1-.1L19.8 7l-.1.1a1.7 1.7 0 0 0-.3 1.9 1.7 1.7 0 0 0 1.6 1h.2v4H21a1.7 1.7 0 0 0-1.6 1Z" />
      </>
    ),
    admin: (
      <>
        <path d="M12 3 4.5 6v5c0 4.7 3.2 8.1 7.5 10 4.3-1.9 7.5-5.3 7.5-10V6z" />
        <path d="m9 12 2 2 4-4" />
      </>
    ),
    logout: <path d="M10 4H5v16h5M14 8l4 4-4 4M18 12H9" />,
  };

  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="h-5 w-5 shrink-0"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {paths[name]}
    </svg>
  );
}
